import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle, ShieldOff } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const LAST_UPDATED = "8 de febrero de 2026";

export default function Terminos() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <header className="max-w-3xl mx-auto w-full px-5 pt-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
          data-testid="terminos-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Inicio
        </button>
        <BrandMark size="sm" />
      </header>

      <div className="max-w-3xl mx-auto w-full px-5 pt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-zinc-900 flex items-center justify-center">
            <FileText className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black text-zinc-700">Documento legal</p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight">
              Términos y Condiciones
            </h1>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mb-6">Última actualización: {LAST_UPDATED}</p>

        {/* DISCLAIMER PROMINENTE */}
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-5 mb-8" data-testid="terminos-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-black text-red-900 mb-1 text-lg">Lee esto antes de usar la plataforma</h3>
              <p className="text-sm text-red-900 leading-relaxed">
                <strong>NO somos abogados</strong>, no somos un bufete jurídico ni una firma especializada en inmigración. Nuestro servicio se centra <strong>exclusivamente en información didáctica y educacional</strong>. <strong>No nos hacemos responsables del resultado</strong> que esta información pueda tener si es aplicada en algún proceso legal o administrativo. Para asesoría legal acude siempre a un abogado certificado o a una organización pro bono acreditada (las listamos en nuestro directorio dentro del módulo de Recursos).
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-zinc max-w-none space-y-6 text-zinc-800 leading-relaxed">

          <Section title="1. Aceptación de los términos">
            <p>
              Al registrarte, comprar un plan o usar cualquier funcionalidad de <strong>miapoyo.com · Defiende Tus Derechos</strong>, manifiestas que has leído, comprendido y aceptado íntegramente los presentes Términos y Condiciones. Si no estás de acuerdo, abstente de usar la plataforma.
            </p>
          </Section>

          <Section title="2. Identidad del prestador del servicio">
            <p>
              El servicio es operado por <strong>MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V.</strong>, con domicilio fiscal en <strong>Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México</strong>.
            </p>
          </Section>

          <Section title="3. Naturaleza del servicio · NO es asesoría legal">
            <p>
              <strong>miapoyo</strong> es una plataforma tecnológica que utiliza inteligencia artificial para entregar información educativa y didáctica sobre derechos de inmigrantes en Estados Unidos. Operamos bajo la doctrina constitucional de auto-representación civil (pro se) y los Estándares Nacionales de Detención del ICE (PBNDS 2011 §&nbsp;6.3 «Law Libraries and Legal Material»).
            </p>
            <p>
              Las funciones que ofrecemos consisten en lectura asistida de manuales oficiales públicos, traducción de términos legales del inglés al español sencillo, transcripción de datos públicos del usuario en formularios estándar, y orientación general basada en fuentes oficiales.
            </p>
            <p className="bg-amber-50 border-l-4 border-amber-600 p-3 rounded-r not-prose">
              <strong>Bajo ningún supuesto el servicio sustituye la asesoría legal personalizada</strong> de un abogado certificado o de una organización de asistencia legal acreditada. La información provista es general y educativa, no constituye consejo legal estratégico ni representación.
            </p>
          </Section>

          <Section title="4. Exclusión de responsabilidad" testId="terminos-section-responsabilidad">
            <div className="bg-zinc-900 text-white rounded-xl p-5 not-prose mb-3">
              <div className="flex items-center gap-2 mb-2">
                <ShieldOff className="w-5 h-5 text-red-400" />
                <p className="font-black uppercase tracking-widest text-xs text-red-400">Limitación de responsabilidad</p>
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">
                MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V., sus accionistas, directivos, empleados y proveedores, NO se hacen responsables del resultado que la información provista pueda tener si es aplicada en algún proceso legal, migratorio o administrativo.
              </p>
            </div>
            <p>El usuario reconoce y acepta expresamente que:</p>
            <p>a) La decisión de actuar o no con base en la información recibida es <strong>exclusiva y personal del usuario</strong>.</p>
            <p>b) Los procesos migratorios son <strong>complejos y dinámicos</strong>; las leyes pueden cambiar sin previo aviso.</p>
            <p>c) Cada caso es <strong>único</strong> y requiere análisis individualizado que solo un abogado puede ofrecer.</p>
            <p>d) La plataforma NO garantiza ningún resultado ante autoridades migratorias, cortes de inmigración (EOIR), ni cualquier autoridad gubernamental.</p>
            <p>e) Cualquier formulario llenado con apoyo de la plataforma debe ser revisado por el propio usuario antes de su presentación; la responsabilidad de su contenido es del firmante.</p>
            <p>f) La inteligencia artificial puede cometer errores; siempre verifica los datos críticos contra las fuentes oficiales (USCIS, EOIR, ACLU).</p>
          </Section>

          <Section title="5. Compromiso de honestidad del usuario">
            <p>
              El usuario se obliga a no utilizar la plataforma para llenar formularios oficiales con información falsa. Mentir en un formulario migratorio es delito federal en EE.UU. y puede destruir el caso del usuario y resultar en remoción acelerada. La Empresa no es responsable de la veracidad de los datos que el usuario decida ingresar en formularios oficiales.
            </p>
          </Section>

          <Section title="6. Cuenta, PIN y seguridad">
            <p>
              Te registras con un pseudónimo y una contraseña que tú eliges. Al confirmarse tu pago se te asigna un PIN único de seis dígitos. <strong>El PIN es personal e intransferible</strong>. Eres responsable de mantenerlo en secreto. La Empresa no se hace responsable del uso indebido de tu PIN por terceros con quienes lo hayas compartido.
            </p>
          </Section>

          <Section title="7. Planes, precios y llamadas">
            <p>
              Los planes y precios vigentes son: <strong>Personal $49 USD</strong> (1 llamada de emergencia + 1 llamada en centro de detención), <strong>Familiar $120 USD</strong> (4 llamadas de cada tipo, hasta 4 miembros), <strong>Llamada adicional $30 USD</strong>. Los pagos son <strong>únicos, no recurrentes</strong>, y se realizan en dólares estadounidenses.
            </p>
            <p>
              Cada llamada habilitada puede usarse <strong>una sola vez</strong> por persona y por escenario. Esta restricción es necesaria para garantizar que el servicio llegue efectivamente a quien lo necesita.
            </p>
          </Section>

          <Section title="8. Política de reembolsos">
            <p>
              Dado el carácter inmediato y digital del servicio, los pagos confirmados <strong>no son reembolsables</strong> salvo en caso de error técnico verificable atribuible a la Empresa, en cuyo caso emitiremos el crédito correspondiente en un plazo no mayor a 15 días hábiles.
            </p>
          </Section>

          <Section title="9. Métodos de pago">
            <p>
              Aceptamos tarjeta de débito/crédito vía Stripe, Western Union, MoneyGram y Remitly. Para depósitos en efectivo, el beneficiario es <strong>Ricardo Zermeño Mejía</strong>, en Guadalajara, Jalisco, México. Una vez hecho el depósito el usuario debe registrar su MTCN y monto exacto en la plataforma. La verificación manual del pago se realiza en un máximo de 24 horas hábiles.
            </p>
          </Section>

          <Section title="10. Uso aceptable">
            <p>El usuario se obliga a NO:</p>
            <p>a) Compartir su PIN con personas ajenas a su plan.</p>
            <p>b) Utilizar la plataforma para fines ilícitos o contrarios a la moral.</p>
            <p>c) Realizar ingeniería inversa, hacking, scraping automatizado o intentos de vulnerar la seguridad.</p>
            <p>d) Suplantar la identidad de la Empresa o de otros usuarios.</p>
            <p>e) Grabar las conversaciones con el asistente y redistribuirlas con fines comerciales.</p>
            <p>El incumplimiento puede resultar en la cancelación inmediata de la cuenta sin reembolso.</p>
          </Section>

          <Section title="11. Propiedad intelectual">
            <p>
              Todos los contenidos, marca, código fuente, diseños y materiales de la plataforma son propiedad de MARKETING DE INTELIGENCIA ARTIFICIAL S. de R.L. de C.V. o de sus licenciantes. Los manuales oficiales referenciados (PBNDS, EOIR, USCIS) son de dominio público y se citan únicamente con fines educativos.
            </p>
          </Section>

          <Section title="12. Modificaciones">
            <p>
              La Empresa puede modificar estos Términos en cualquier momento. Los cambios entran en vigor al publicarse en esta página con la fecha actualizada. El uso continuado de la plataforma posterior a la publicación implica aceptación de los nuevos Términos.
            </p>
          </Section>

          <Section title="13. Jurisdicción y ley aplicable">
            <p>
              Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Para cualquier controversia las partes se someten expresamente a los tribunales competentes de la ciudad de Guadalajara, Jalisco, México, renunciando a cualquier otro fuero que pudiera corresponderles por razón de domicilio o cualquier otra causa.
            </p>
          </Section>

          <Section title="14. Contacto">
            <p>
              Para cualquier asunto relacionado con estos Términos, contacta a <strong>Ricardo Zermeño Mejía</strong> en Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México.
            </p>
          </Section>
        </div>
      </div>
      <DisclaimerBanner />
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-xl sm:text-2xl font-black text-zinc-900 mt-6 mb-2 tracking-tight">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
